import { ChangeDetectionStrategy, Component, input, OnDestroy, inject, computed, output, signal } from '@angular/core';
import { Router } from '@angular/router';
import { SvgIconComponent } from '@ngneat/svg-icon';

import { AvatarsComponent, SplitActionButtonComponent, SplitActionMenuItem, TooltipModule } from '@libs/ui-lib';
import { Alert, TagEvent, TagPusherService } from '@libs/shared-services';
import { TranslateModule } from '@ngx-translate/core';
import { DatePipe, NgClass } from '@angular/common';
import { PhoneFormatPipe } from '@libs/patients';
import {
  healthHistoryActions,
  NurturingFeature,
  selectIsEaglesoftPms,
  selectProvisionalLocksEditing,
  selectSiteSettingsPmsType,
} from '@libs/store';
import { NurturingDirective, NurturingStore } from '@libs/nurturing';
import { Store } from '@ngrx/store';
import { PmsType } from '@ffd/api-client';
import { routes } from '@libs/utils';
import { AppointmentStatusChipComponent } from '../schedule-calendar';
import { TimeRangeFormatPipe } from '../time-range-format';
import { ScheduleAlertComponent } from './schedule-alert/schedule-alert.component';
import { ScheduleDetailsVM } from './schedule-details.vm';

@Component({
  selector: 'lib-schedule-details',
  imports: [
    SvgIconComponent,
    SplitActionButtonComponent,
    TranslateModule,
    TimeRangeFormatPipe,
    DatePipe,
    TooltipModule,
    NgClass,
    PhoneFormatPipe,
    AvatarsComponent,
    AppointmentStatusChipComponent,
    ScheduleAlertComponent,
    NurturingDirective,
  ],
  templateUrl: './schedule-details.component.html',
  styleUrl: './schedule-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduleDetailsComponent implements OnDestroy {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly tagPusherService = inject(TagPusherService);
  private readonly nurturingStore = inject(NurturingStore);

  public vm = input.required<ScheduleDetailsVM>();
  /**
   * Whether the container rendering these details is on screen. The right sidebar keeps its content
   * mounted while collapsed, so the arrow — which lives in a CDK overlay outside the sidebar's box —
   * would otherwise stay visible pointing at a hidden button.
   */
  public readonly isVisible = input(true);

  /**
   * Guided tour step 1 coachmark, anchored to the Chart button while this panel is on screen. Its
   * "Next" button dismisses the step and opens the chart of the patient in this panel, whichever
   * appointment that is. `visibleNurturingFeatures` already excludes features that are dismissed or
   * inside their temporary dismissal window.
   */
  protected readonly guidedTourFeature = NurturingFeature.GuidedTourStep1AppointmentNudge;

  private readonly pmsType = this.store.selectSignal(selectSiteSettingsPmsType);
  /**
   * Open Dental and Eaglesoft manage their own appointment status types, so the status is shown with the name the PMS
   * provides instead of the FFD status chip.
   */
  public readonly usesPmsStatusDisplayName = computed(() => this.pmsType() === PmsType.OPEN_DENTAL || this.pmsType() === PmsType.EAGLESOFT);
  protected readonly isEaglesoftPms = this.store.selectSignal(selectIsEaglesoftPms);

  private readonly provisionalLocksEditing = this.store.selectSignal(selectProvisionalLocksEditing);
  /**
   * Appointments only carry an `isProvisional` flag, with no provisional status, so there is no pending delete to keep
   * locked here — see `isLockedByProvisional` for the models that do.
   */
  protected readonly isEditLockedByProvisional = computed(
    () => this.vm().appointmentDetails.isProvisional && this.provisionalLocksEditing() && this.vm().siteId !== routes.showcaseSiteId,
  );

  public readonly avatarClick = output<void>();
  public readonly editClick = output<void>();

  protected readonly isExpanded = signal(true);
  protected expandedAlertIndex: number | null = null;

  protected readonly menuItems = computed<SplitActionMenuItem[]>(() => {
    const vmValue = this.vm();
    return [
      {
        label: 'schedule_details_chart',
        icon: 'px-24-chart',
        action: () => this.onChartMenuItemClick(vmValue.siteId, vmValue.patientId!),
      },
      {
        label: 'schedule_details_digital',
        icon: 'px-32-digital',
        action: () => this.onDigitalMenuItemClick(vmValue.siteId, vmValue.patientId!),
      },
      {
        label: 'schedule_details_perio',
        icon: 'px-32-perio',
        action: () => this.onPerioMenuItemClick(vmValue.siteId, vmValue.patientId!),
      },
      {
        label: 'schedule_details_notes',
        icon: 'px-24-edit-note',
        action: () => this.onNotesMenuItemClick(vmValue.siteId, vmValue.patientId!),
      },
      {
        label: 'schedule_details_collab',
        icon: 'px-24-collab',
        action: () => this.onCollabMenuItemClick(vmValue.siteId, vmValue.patientId!),
      },
      {
        label: 'schedule_details_patient_profile',
        icon: 'px-24-history',
        action: () => this.openHealthHistoryModal(),
      },
    ];
  });

  protected readonly primaryChartAction = computed<() => void>(() => {
    const { siteId, patientId } = this.vm();
    return () => this.onChartMenuItemClick(siteId, patientId!);
  });

  /**
   * "Next" on the guided tour coachmark: permanently dismisses this step, then opens the chart of the
   * patient in this panel, where the tour continues.
   */
  protected onGuidedTourNext(): void {
    this.nurturingStore.dismissNurturingFeature(this.guidedTourFeature);

    const { siteId, patientId } = this.vm();

    if (patientId) {
      this.onChartMenuItemClick(siteId, patientId);
    }
  }

  public ngOnDestroy(): void {
    this.store.dispatch(healthHistoryActions.resetHealthHistoryAndPatientAlerts());
  }
  public alerts = computed((): Alert[] => {
    const vmValue = this.vm();
    const alerts: Alert[] = [...vmValue.patientAlertItems];
    if (vmValue.medicalAlertItem) {
      alerts.unshift(vmValue.medicalAlertItem);
    }

    return alerts;
  });

  public onAvatarClick(): void {
    if (this.vm().appointmentDetails.isPreRegistrationAppointment) {
      return;
    }

    this.avatarClick.emit();
  }

  public onEditClick(): void {
    if (this.isEditLockedByProvisional()) {
      return;
    }

    this.editClick.emit();
  }

  public onClearExpandedIndex(): void {
    this.expandedAlertIndex = null;
  }

  public onToggleAlert(alertIndex: number): void {
    this.expandedAlertIndex = this.expandedAlertIndex === alertIndex ? null : alertIndex;
  }

  public isAlertExpanded(alertIndex: number): boolean {
    return this.expandedAlertIndex === alertIndex;
  }

  protected openHealthHistoryModal(): void {
    this.avatarClick.emit();
  }
  protected onChartMenuItemClick(siteId: string, patientId: string): void {
    this.router.navigate([routes.chartingOnSiteForPatient(siteId, patientId)]);
    this.tagPusherService.pushTag({
      event: TagEvent.ChartingOpened,
      location: 'schedule',
      patient_id: patientId,
    });
  }
  protected onDigitalMenuItemClick(siteId: string, patientId: string): void {
    this.router.navigate([routes.imagingOnSiteForPatient(siteId, patientId)]);
  }

  protected onPerioMenuItemClick(siteId: string, patientId: string): void {
    this.router.navigate([routes.perioOnSiteForPatient(siteId, patientId)]);
  }

  protected onNotesMenuItemClick(siteId: string, patientId: string): void {
    this.router.navigate([routes.notesOnSiteForPatient(siteId, patientId)]);
  }

  protected onCollabMenuItemClick(siteId: string, patientId: string): void {
    this.router.navigate([routes.collabOnSiteForPatient(siteId, patientId)]);
  }
}
